// file: src/pages/AdminUsers.jsx
import React, { useMemo, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createUser, listUsers, deleteUser, updateUser } from "../api/adminApi";
import { initFirebase, ensureFirebaseUser } from "../lib/firebase.js";
import Loader from "../components/Loader.js";

/* Theme */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

/* Utils */
const clsx = (...p) => p.filter(Boolean).join(" ");
const nowIso = () => new Date().toISOString();
const uid = () => Math.random().toString(36).slice(2, 10);
const qmatch = (s, q) => s?.toLowerCase().includes(q.toLowerCase().trim());
const fmt = (iso) => new Date(iso).toLocaleString();
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const isEmpty = (v) =>
  v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");

const IcRefresh = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v6h-6" />
  </svg>
);

const IcTrash = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);
const IcEdit = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5z" />
  </svg>
);

/* Icons */
const IcPlus = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IcSearch = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);
const IcClose = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <path d="M6 6l12 12M6 18L18 6" />
  </svg>
);
const IcUser = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <circle cx="12" cy="7" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);
const IcBuilding = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    {...p}
  >
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M4 19h16" />
  </svg>
);

// Better Eye / EyeOff icons (rounded, crisp, 24x24)
export const IcEye = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    {/* Eye outline */}
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
    {/* Iris */}
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IcEyeOff = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...p}
  >
    {/* Slash */}
    <path d="M1 1l22 22" />
    {/* Left half of eye outline */}
    <path d="M1 12s4-8 11-8a11 11 0 0 1 7.31 2.69" />
    {/* Right half of eye outline */}
    <path d="M23 12s-4 8-11 8a11 11 0 0 1-7.31-2.69" />
    {/* Pupil (broken to suggest hidden) */}
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
  </svg>
);

/* Badges */
const Chip = ({ tone = "slate", children }) => {
  const map = {
    slate: ["bg-slate-50", "text-slate-700", "ring-slate-100"],
    emerald: ["bg-emerald-50", "text-emerald-700", "ring-emerald-100"],
    indigo: ["bg-indigo-50", "text-indigo-700", "ring-indigo-100"],
  }[tone];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1",
        ...map,
      )}
    >
      {children}
    </span>
  );
};

/** Map Firebase/REST auth errors to friendly messages */
function friendlyAuthError(err) {
  const code =
    err?.code ||
    err?.payload?.code ||
    err?.payload?.error?.message || // REST: e.g. "EMAIL_EXISTS"
    "";

  const msg = String(err?.message || "").toLowerCase();

  // Common Firebase web SDK codes
  if (code === "auth/email-already-in-use") {
    return "That email is already in use. If this user exists in another portal, use a different email.";
  }
  if (code === "auth/invalid-email") return "Enter a valid email address.";
  if (code === "auth/weak-password")
    return "Password is too weak (min 6–8+ chars).";
  if (code === "auth/network-request-failed")
    return "Network error. Check your connection and try again.";
  if (code === "auth/operation-not-allowed")
    return "Email/password sign-in is not enabled for this project.";
  if (code === "auth/invalid-credential") {
    // Often returned when the email already exists in another tenant/project or custom flow
    return "That email can’t be used here because it already exists (possibly in another portal). Use a different email.";
  }

  // Google Identity Toolkit/REST messages
  const rest = String(code).toUpperCase();
  if (rest.includes("EMAIL_EXISTS")) {
    return "That email is already in use. Try a different email.";
  }
  if (rest.includes("INVALID_EMAIL")) return "Enter a valid email address.";
  if (rest.includes("WEAK_PASSWORD")) return "Password is too weak.";
  if (rest.includes("OPERATION_NOT_ALLOWED")) {
    return "Email/password sign-in is not enabled for this project.";
  }

  // Fallbacks if the message reveals the issue
  if (msg.includes("already in use")) {
    return "That email is already in use.";
  }

  return err?.message || "Something went wrong. Please try again.";
}

// Replace your FacilityPill with this one
function FacilityPill({ id, name }) {
  const fullLabel = [id ? `#${id}` : null, name ? name : null]
    .filter(Boolean)
    .join(" • ");

  const visible = name || (id ? `#${id}` : "—"); // prefer facility name

  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-slate-200 text-slate-700 max-w-[220px]" // <-- set a max width
      title={fullLabel}
    >
      <span className="truncate">{visible}</span> {/* <-- ellipsis */}
    </span>
  );
}

/* Modal (scrollable content, sticky header, mobile-friendly full height) */
function Modal({ open, onClose, title, children }) {
  const overlayRef = useRef(null);
  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={(e) => e.target === overlayRef.current && onClose()}
      className="fixed inset-0 z-50 grid place-items-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      {/* Dim + slight blur background */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      {/* Card: flex column + max height; full-height sheet on mobile */}
      <div
        className="
          relative z-10 flex w-full max-w-2xl flex-col
          rounded-none border border-black/[0.06] bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)]
          max-h-[90vh]
          sm:rounded-3xl
        "
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 py-3 sm:px-6 sm:py-4 border-b border-black/[0.06]">
          <h3 className="text-base sm:text-lg font-semibold tracking-tight">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-black/10 hover:bg-black/[0.03] transition"
            aria-label="Close"
          >
            <IcClose className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          {children}
        </div>
      </div>
    </div>
  );
}

function FacilitiesCell({ items = [] }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return <span className="text-xs text-black/50">—</span>;

  const MAX = 3;
  const head = list.slice(0, MAX);
  const rest = list.length - head.length;

  const allText = list
    .map((f) =>
      [f.facility_id && `#${f.facility_id}`, f.facility_name]
        .filter(Boolean)
        .join(" • "),
    )
    .join(", ");

  return (
    <div className="max-w-[560px]" title={allText}>
      <div className="flex flex-wrap gap-1.5">
        {head.map((f, i) => (
          <FacilityPill
            key={`${f.facility_id}-${f.facility_name}-${i}`}
            id={f.facility_id}
            name={f.facility_name}
          />
        ))}
        {rest > 0 && (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-slate-200 text-slate-600 bg-slate-50"
            title={allText}
          >
            +{rest} more
          </span>
        )}
      </div>
    </div>
  );
}
function ClampWrap({ children, collapsedLines = 2 }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="max-w-[560px]">
      <div
        className={clsx("relative transition-all", open ? "" : "line-clamp-2")}
        style={!open ? { WebkitLineClamp: clamp(collapsedLines, 1, 4) } : {}}
      >
        {children}
      </div>

      {/* Toggle only if content likely overflows */}
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="mt-1 text-xs font-medium text-slate-600 hover:text-slate-800"
      >
        {open ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

function Avatar({ email = "", size = 28 }) {
  const ch = (email || "?").trim()[0]?.toUpperCase() || "?";
  // stable hue from email
  let hash = 0;
  for (let i = 0; i < email.length; i++)
    hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  const bg = `hsl(${hue} 70% 45%)`;
  return (
    <div
      className="grid place-items-center rounded-full text-white font-semibold shrink-0"
      style={{ width: size, height: size, background: bg }}
      aria-hidden
      title={email}
    >
      {ch}
    </div>
  );
}

/* Selector card */
function TypeCard({ icon: Icon, title, subtitle, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-2xl border border-black/[0.06] bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div
        className="grid h-12 w-12 place-items-center rounded-xl text-white shadow"
        style={{
          background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
          boxShadow: `0 8px 18px ${BLUE}24`,
        }}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-black/60">{subtitle}</div>
      </div>
    </button>
  );
}

/* Password field with toggle */
function PasswordField({ name, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Field
        name={name}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 pr-10 outline-none focus:ring-4"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-2 grid place-items-center text-black/60"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? (
          <IcEyeOff className="h-5 w-5" />
        ) : (
          <IcEye className="h-5 w-5" />
        )}
      </button>
      <ErrorMessage
        name={name}
        component="div"
        className="mt-1 text-xs text-rose-600"
      />
    </div>
  );
}

/* Pagination */
function Pager({ page, total, pageSize, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) return null;
  const prev = () => onChange(Math.max(1, page - 1));
  const next = () => onChange(Math.min(pageCount, page + 1));
  return (
    <div className="mt-4 flex items-center justify-between">
      <div className="text-sm text-black/60">
        Page <span className="font-medium text-black">{page}</span> of{" "}
        <span className="font-medium text-black">{pageCount}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={prev}
          className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
          disabled={page === 1}
        >
          Prev
        </button>
        <button
          onClick={next}
          className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
          disabled={page === pageCount}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* Facilities editor – advanced, required, per-row validation, reordering */
function FacilitiesEditor({
  value = [],
  onChange,
  errorText,
  required = true,
}) {
  const rows =
    Array.isArray(value) && value.length
      ? value
      : required
        ? [{ facility_id: "", facility_name: "" }]
        : [];

  const [touched, setTouched] = React.useState(() => rows.map(() => false));

  const markTouched = (i) =>
    setTouched((t) => {
      const next = [...t];
      next[i] = true;
      return next;
    });

  const update = (i, key, val) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, { facility_id: "", facility_name: "" }]);
    setTouched((t) => [...t, false]);
  };

  const removeRow = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(
      next.length
        ? next
        : required
          ? [{ facility_id: "", facility_name: "" }]
          : [],
    );

    setTouched((t) =>
      t.length > 1 ? t.filter((_, idx) => idx !== i) : [false],
    );
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const tx = [...touched];
    [next[i], next[j]] = [next[j], next[i]];
    [tx[i], tx[j]] = [tx[j], tx[i]];
    onChange(next);
    setTouched(tx);
  };

  const rowInvalid = (r) => {
    const empty = !(
      String(r.facility_id || "").trim() || String(r.facility_name || "").trim()
    );
    return required ? empty : false; // optional never marks empty as invalid
  };

  return (
    <div className="mt-2 rounded-2xl border border-black/[0.06] bg-white/70 backdrop-blur p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800">Facilities</span>
          <span className="rounded-full px-2 py-0.5 text-xs ring-1 ring-slate-200 text-slate-700">
            {rows.length}
          </span>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="rounded-xl px-3 py-2 text-xs font-semibold text-white shadow transition sm:text-sm"
          style={{
            background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
            boxShadow: `0 10px 20px ${BLUE}22`,
          }}
        >
          + Add facility
        </button>
      </div>

      {/* Required help */}
      <p className="mt-1 text-[11px] text-black/55">
        {required ? (
          <>
            <strong>Required:</strong> add at least one facility. Each row needs
            an <em>ID</em> or a <em>Name</em> (you can enter both).
          </>
        ) : (
          <>
            <strong>Optional:</strong> you can leave facilities empty. If you
            add a row, enter an <em>ID</em> or a <em>Name</em>.
          </>
        )}
      </p>

      {/* Global error from schema */}
      {errorText ? (
        <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {errorText}
        </div>
      ) : null}

      {/* Rows */}
      <div className="mt-3 space-y-3">
        {rows.map((r, i) => {
          const invalid = rowInvalid(r) && touched[i];
          return (
            <div
              key={i}
              className={clsx(
                "rounded-2xl border bg-white px-3 py-3 sm:px-4 sm:py-4 shadow-sm transition",
                invalid
                  ? "border-rose-300 ring-1 ring-rose-200"
                  : "border-black/[0.06] ring-1 ring-black/[0.02] hover:shadow-md",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold tracking-wide text-slate-600">
                  Facility #{i + 1}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    className="rounded-md border border-black/[0.08] px-2 py-1 text-[11px] text-slate-700 hover:bg-black/[0.03] disabled:opacity-40"
                    disabled={i === 0}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, +1)}
                    className="rounded-md border border-black/[0.08] px-2 py-1 text-[11px] text-slate-700 hover:bg-black/[0.03] disabled:opacity-40"
                    disabled={i === rows.length - 1}
                  >
                    ↓
                  </button>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-100"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-2 grid gap-2 sm:grid-cols-[1fr,1fr]">
                <div>
                  <label className="block text-xs font-medium text-black/70">
                    Facility ID
                  </label>
                  <input
                    value={r.facility_id || ""}
                    onChange={(e) => update(i, "facility_id", e.target.value)}
                    onBlur={() => markTouched(i)}
                    placeholder="e.g. 1025"
                    className={clsx(
                      "mt-1 w-full rounded-xl border bg-white px-3 py-2 outline-none focus:ring-4",
                      invalid ? "border-rose-300" : "border-black/[0.06]",
                    )}
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-black/70">
                    Facility Name
                  </label>
                  <input
                    value={r.facility_name || ""}
                    onChange={(e) => update(i, "facility_name", e.target.value)}
                    onBlur={() => markTouched(i)}
                    placeholder="e.g. 4909 N Kenmore Ave."
                    className={clsx(
                      "mt-1 w-full rounded-xl border bg-white px-3 py-2 outline-none focus:ring-4",
                      invalid ? "border-rose-300" : "border-black/[0.06]",
                    )}
                  />
                </div>
              </div>

              {invalid ? (
                <div className="mt-2 text-[11px] text-rose-600">
                  Enter an <strong>ID</strong> or a <strong>Name</strong> for
                  this facility.
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TowingFacilitiesCell({ items = [] }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return (
      <span className="text-xs text-slate-500 italic">
        No facility assigned
      </span>
    );
  }

  const MAX = 2;
  const head = list.slice(0, MAX);
  const rest = list.length - head.length;

  const allText = list
    .map((f) =>
      [f.facility_id && `#${f.facility_id}`, f.facility_name]
        .filter(Boolean)
        .join(" • "),
    )
    .join(", ");

  return (
    <div className="max-w-[560px]" title={allText}>
      <div className="flex flex-wrap gap-1.5">
        {head.map((f, i) => (
          <FacilityPill
            key={`${f.facility_id}-${f.facility_name}-${i}`}
            id={f.facility_id}
            name={f.facility_name}
          />
        ))}

        {rest > 0 && (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-slate-200 text-slate-600 bg-slate-50"
            title={allText}
          >
            +{rest} more
          </span>
        )}
      </div>
    </div>
  );
}

function TowingRowActions({ user, onEdit, onReset, onDelete }) {
  return (
    <div className="flex justify-end gap-1.5">
      <button
        onClick={onEdit}
        className="group inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
        title="Edit facilities"
        aria-label="Edit facilities"
      >
        <IcEdit className="h-4 w-4" />
        <span className="hidden lg:inline">Edit</span>
      </button>

      <button
        onClick={onReset}
        className="group inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
        title="Reset password"
        aria-label="Reset password"
      >
        <IcRefresh className="h-4 w-4" />
        <span className="hidden lg:inline">Reset</span>
      </button>

      <button
        onClick={onDelete}
        className="group inline-flex h-8 items-center gap-1.5 rounded-lg bg-rose-600 px-2.5 text-xs font-semibold text-white hover:brightness-105 active:scale-[0.98] transition"
        title="Delete user"
        aria-label="Delete user"
      >
        <IcTrash className="h-4 w-4" />
        <span className="hidden lg:inline">Delete</span>
      </button>
    </div>
  );
}

function LandlordCard({ user, onEdit, onReset, onDelete }) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm hover:shadow transition">
      <div className="flex items-center gap-2">
        <Chip tone="emerald">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          Landlord
        </Chip>
        <span className="ml-auto text-xs text-slate-500">
          {fmt(user.createdAt)}
        </span>
      </div>

      <div className="mt-2 break-all text-sm font-medium">{user.email}</div>

      <div className="mt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
          Facilities
        </div>
        <FacilitiesCell items={user.facilities} />
      </div>

      {/* Actions for mobile */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <button
          onClick={onEdit}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
          aria-label="Edit facilities"
        >
          <IcEdit className="h-4 w-4" />
          <span>Edit</span>
        </button>

        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
        >
          <IcRefresh className="h-4 w-4" />
          <span>Reset</span>
        </button>

        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:brightness-105 active:scale-[0.98] transition"
        >
          <IcTrash className="h-4 w-4" />
          <span>Delete</span>
        </button>
      </div>
    </div>
  );
}

function TowingUsersSection({
  tPage,
  towersFiltered,
  pageT,
  pageSize,
  setPageT,
  openEditFor,
  openResetFor,
  openDeleteFor,
}) {
  return (
    <section className="relative rounded-3xl border border-black/[0.06] bg-white shadow-sm overflow-hidden">
      {/* Desktop/Table */}
      <div
        className={`
          hidden md:block overflow-x-auto
          [scrollbar-width:none] [-ms-overflow-style:none]
          [&::-webkit-scrollbar]:hidden
        `}
      >
        <table className="w-full text-left">
          {/* Give the table sane column widths so it doesn't blow out */}
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 240 }} />
            <col style={{ width: 320 }} />
            <col />
            <col style={{ width: 190 }} />
            <col style={{ width: 170 }} />
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50/80 backdrop-blur border-b border-slate-100">
              <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Type
              </th>
              <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Company
              </th>
              <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Email
              </th>
              <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Facilities
              </th>
              <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Created
              </th>
              <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="text-[13.5px]">
            {tPage.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-slate-500"
                >
                  No towing users found.
                </td>
              </tr>
            ) : (
              tPage.map((u, idx) => (
                <tr
                  key={u.id}
                  className={[
                    "border-b border-slate-100 transition",
                    idx % 2 ? "bg-white" : "bg-slate-50/[0.25]",
                    "hover:bg-white",
                    "group",
                  ].join(" ")}
                >
                  <td className="px-6 py-4 align-top">
                    <Chip tone="indigo">
                      <span className="h-2 w-2 rounded-full bg-indigo-500" />
                      Towing
                    </Chip>
                  </td>

                  <td className="px-6 py-4 align-top">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="shrink-0">
                        <div
                          className="grid place-items-center rounded-xl text-white shadow-sm"
                          style={{
                            width: 32,
                            height: 32,
                            background:
                              "linear-gradient(135deg, rgb(99 102 241), rgb(59 130 246))",
                          }}
                          aria-hidden
                          title={u.companyName}
                        >
                          <IcBuilding className="h-4 w-4" />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div
                          className="font-semibold text-slate-900 truncate"
                          title={u.companyName}
                        >
                          {u.companyName || "—"}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          UID: {u.firebase_uid?.slice(0, 8) || "—"}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 align-top">
                    <div className="min-w-0">
                      <div
                        className="truncate font-medium text-slate-900"
                        title={u.email}
                      >
                        {u.email}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                        <span className="rounded-full bg-slate-50 px-2 py-0.5 ring-1 ring-slate-200">
                          {u.companyName ? "Company user" : "Towing user"}
                        </span>
                        <span className="rounded-full bg-slate-50 px-2 py-0.5 ring-1 ring-slate-200">
                          {Array.isArray(u.facilities)
                            ? u.facilities.length
                            : 0}{" "}
                          facilities
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 align-top">
                    <TowingFacilitiesCell items={u.facilities} />
                  </td>

                  <td className="px-6 py-4 align-top whitespace-nowrap text-slate-700">
                    {fmt(u.createdAt)}
                  </td>

                  {/* Compact actions: icons only so it doesn't push the table width */}
                  <td className="px-6 py-4 align-top">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditFor(u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                        title="Edit facilities"
                        aria-label="Edit facilities"
                      >
                        <IcEdit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => openResetFor("towing", u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                        title="Reset password"
                        aria-label="Reset password"
                      >
                        <IcRefresh className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => openDeleteFor("towing", u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-600 text-white hover:brightness-105 active:scale-[0.98] transition"
                        title="Delete user"
                        aria-label="Delete user"
                      >
                        <IcTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile/Cards */}
      <div className="md:hidden p-4">
        <div className="grid gap-3">
          {tPage.length === 0 ? (
            <div className="rounded-2xl border border-black/[0.06] bg-white p-6 text-center text-black/60">
              No towing users found.
            </div>
          ) : (
            tPage.map((u) => (
              <div
                key={u.id}
                className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm hover:shadow transition"
              >
                <div className="flex items-center gap-2">
                  <Chip tone="indigo">
                    <span className="h-2 w-2 rounded-full bg-indigo-500" />
                    Towing
                  </Chip>
                  <span className="ml-auto text-xs text-slate-500">
                    {fmt(u.createdAt)}
                  </span>
                </div>

                <div className="mt-2">
                  <div
                    className="text-sm font-semibold text-slate-900 truncate"
                    title={u.companyName}
                  >
                    {u.companyName || "—"}
                  </div>

                  <div className="mt-1 flex items-center gap-3">
                    <Avatar email={u.email} />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-700 break-all">
                        {u.email}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        UID: {u.firebase_uid?.slice(0, 8) || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Facilities
                  </div>
                  <TowingFacilitiesCell items={u.facilities} />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <button
                    onClick={() => openEditFor(u)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                  >
                    <IcEdit className="h-4 w-4" />
                    <span>Edit</span>
                  </button>

                  <button
                    onClick={() => openResetFor("towing", u)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                  >
                    <IcRefresh className="h-4 w-4" />
                    <span>Reset</span>
                  </button>

                  <button
                    onClick={() => openDeleteFor("towing", u)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:brightness-105 active:scale-[0.98] transition"
                  >
                    <IcTrash className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="px-4 pb-4 pt-2">
        <Pager
          page={pageT}
          total={towersFiltered.length}
          pageSize={pageSize}
          onChange={setPageT}
        />
      </div>
    </section>
  );
}

/* Main */
export default function AdminUsers() {
  const [tab, setTab] = useState("landlord");
  const [query, setQuery] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [landlords, setLandlords] = useState([]);
  const [towers, setTowers] = useState([]);

  const [openReset, setOpenReset] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingApi, setDeletingApi] = useState(false);

  const [submitError, setSubmitError] = useState("");
  const [submittingApi, setSubmittingApi] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [openEdit, setOpenEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [updatingApi, setUpdatingApi] = useState(false);

  const pageSize = 10;
  const [pageL, setPageL] = useState(1);
  const [pageT, setPageT] = useState(1);

  useEffect(() => {
    initFirebase();
    void loadUsers();
  }, []);

  async function loadUsers() {
    try {
      setLoadingUsers(true);
      const rows = await listUsers();

      const norm = (u) => {
        // be defensive about key names
        const firebaseUid =
          u.firebase_uid ??
          u.firebaseUid ??
          u.uid ??
          u.auth_uid ??
          u.auth_uid ??
          u.auth?.uid ??
          "";

        const rawFacilities = Array.isArray(u.facilities) ? u.facilities : [];
        const facilities = rawFacilities
          .map((f) => ({
            facility_id: String(f.facility_id ?? "").trim(),
            facility_name: String(f.facility_name ?? "").trim(),
          }))
          // drop totally empty rows
          .filter((f) => f.facility_id || f.facility_name);

        return {
          id: u.id || firebaseUid || u.email,
          firebase_uid: firebaseUid,
          email: u.email,
          role: String(u.role || "").toLowerCase(),
          companyName: u.company_name || "",
          createdAt: u.created_at || u.createdAt || new Date().toISOString(),
          facilities, // <-- keep it
        };
      };

      const all = Array.isArray(rows) ? rows.map(norm) : [];
      setLandlords(all.filter((r) => r.role === "landlord"));
      setTowers(all.filter((r) => r.role === "towing_company"));

      // debug: verify what came back
      console.debug("Users (normalized)", all);
    } catch (e) {
      setSubmitError(friendlyAuthError(e));
    } finally {
      setLoadingUsers(false);
    }
  }

  const landlordsFiltered = useMemo(() => {
    return !query.trim()
      ? landlords
      : landlords.filter((u) => qmatch(u.email, query));
  }, [landlords, query]);

  const towersFiltered = useMemo(() => {
    return !query.trim()
      ? towers
      : towers.filter(
          (u) => qmatch(u.email, query) || qmatch(u.companyName, query),
        );
  }, [towers, query]);

  const lStart = (pageL - 1) * pageSize;
  const lPage = landlordsFiltered.slice(lStart, lStart + pageSize);
  const tStart = (pageT - 1) * pageSize;
  const tPage = towersFiltered.slice(tStart, tStart + pageSize);

  const landlordSchema = Yup.object({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(8, "Min 8 characters")
      .required("Password is required"),
    facilities: Yup.array()
      .of(
        Yup.object({
          facility_id: Yup.string().trim().optional(),
          facility_name: Yup.string().trim().optional(),
        }).test(
          "row-has-id-or-name",
          "Each facility row needs an ID or a Name",
          (v) => !!(v?.facility_id?.trim() || v?.facility_name?.trim()),
        ),
      )
      .min(1, "Add at least one facility")
      .required("Facilities are required"),
  });

  const towingSchema = Yup.object({
    companyName: Yup.string().trim().required("Company name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(8, "Min 8 characters")
      .required("Password is required"),

    // ✅ facilities optional: allow empty rows / empty list
    facilities: Yup.array()
      .of(
        Yup.object({
          facility_id: Yup.string().trim().optional(),
          facility_name: Yup.string().trim().optional(),
        }),
      )
      .test(
        "towing-facilities-optional",
        "Each facility row needs an ID or a Name",
        (arr) => {
          if (!arr || arr.length === 0) return true; // empty list OK

          // allow fully empty rows, but if something is typed, require id or name
          return arr.every((r) => {
            const id = (r?.facility_id || "").trim();
            const name = (r?.facility_name || "").trim();
            return !id && !name ? true : Boolean(id || name);
          });
        },
      )
      .optional(),
  });

  const resetSchema = Yup.object({
    password: Yup.string()
      .min(8, "Min 8 characters")
      .required("Password is required"),
  });

  const closeAdd = () => {
    setOpenAdd(false);
    setSelectedType(null);
    setSubmitError("");
    setSubmittingApi(false);
  };
  const openResetFor = (kind, user) => {
    setResetTarget({ kind, ...user });
    setOpenReset(true);
  };
  const openDeleteFor = (kind, user) => {
    setDeleteTarget({ kind, ...user }); // must contain firebase_uid
    setOpenDelete(true);
  };

  async function doDelete() {
    if (!deleteTarget?.firebase_uid) {
      alert("Missing firebase_uid for this user.");
      return;
    }
    try {
      setDeletingApi(true);
      await deleteUser(deleteTarget.firebase_uid);
      await loadUsers();
      setOpenDelete(false);
      setDeleteTarget(null);
    } catch (e) {
      alert(e?.message || "Failed to delete user");
    } finally {
      setDeletingApi(false);
    }
  }

  const openEditFor = (user) => {
    setEditTarget(user); // needs firebase_uid
    setOpenEdit(true);
  };

  return (
    <div className="space-y-6 antialiased text-slate-900">
      {/* Header */}
      <section className="rounded-3xl border border-black/[0.06] bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <label htmlFor="admin-search" className="block text-sm font-medium">
              Search users
            </label>
            <div className="relative mt-1">
              <input
                id="admin-search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPageL(1);
                  setPageT(1);
                }}
                placeholder="Search by email or company name"
                className="w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 outline-none focus:ring-4"
              />
              <IcSearch className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-black/50" />
            </div>
            {!query && (
              <p className="mt-2 text-xs text-black/50">
                Manage Landlord and Towing users. Use tabs to switch lists.
              </p>
            )}
          </div>
          <button
            onClick={() => setOpenAdd(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--blue)] px-4 py-3 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition"
            style={{ "--blue": BLUE, boxShadow: `0 10px 24px ${BLUE}26` }}
          >
            <IcPlus className="h-5 w-5" /> Add New User
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-black/[0.06] p-1 w-full max-w-[520px]">
          <button
            onClick={() => setTab("landlord")}
            className={clsx(
              "flex-1 rounded-lg px-3 py-2 text-sm transition",
              tab === "landlord"
                ? "bg-black/[0.04] font-semibold"
                : "text-black/70 hover:text-black",
            )}
          >
            Landlords{" "}
            <span className="ml-1 text-xs text-black/60">
              ({landlords.length})
            </span>
          </button>
          <button
            onClick={() => setTab("towing")}
            className={clsx(
              "flex-1 rounded-lg px-3 py-2 text-sm transition",
              tab === "towing"
                ? "bg-black/[0.04] font-semibold"
                : "text-black/70 hover:text-black",
            )}
          >
            Towing{" "}
            <span className="ml-1 text-xs text-black/60">
              ({towers.length})
            </span>
          </button>
        </div>
      </section>

      {/* Tables - show a centered loader while fetching */}
      {loadingUsers ? (
        <section className="rounded-3xl p-10 shadow-sm grid place-items-center">
          <Loader />
        </section>
      ) : tab === "landlord" ? (
        <section className="relative rounded-3xl border border-black/[0.06] bg-white p-0 shadow-sm overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              {/* Consistent widths so rows don't jump */}
              <colgroup>
                <col style={{ width: 140 }} />
                <col style={{ width: 420 }} />
                <col />
                <col style={{ width: 210 }} />
                <col style={{ width: 190 }} />
              </colgroup>

              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50/80 backdrop-blur border-b border-slate-100">
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Type
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Email
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Facilities
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="text-[13.5px]">
                {lPage.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      No landlords found.
                    </td>
                  </tr>
                ) : (
                  lPage.map((u, idx) => (
                    <tr
                      key={u.id}
                      className={clsx(
                        "border-b border-slate-100 transition group",
                        idx % 2 ? "bg-white" : "bg-slate-50/[0.25]",
                        "hover:bg-white",
                      )}
                    >
                      {/* Type */}
                      <td className="px-6 py-4 align-top">
                        <Chip tone="emerald">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          Landlord
                        </Chip>
                      </td>

                      {/* Email w/ avatar + ellipsis */}
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar email={u.email} />
                          <div className="min-w-0">
                            <div
                              className="truncate font-medium text-slate-900"
                              title={u.email}
                            >
                              {u.email}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              UID: {u.firebase_uid?.slice(0, 8) || "—"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Facilities (wrap nicely, clamp visually via your chips) */}
                      <td className="px-6 py-4 align-top">
                        <div className="max-w-[560px]">
                          <FacilitiesCell items={u.facilities} />
                        </div>
                      </td>

                      {/* Created */}
                      <td className="px-6 py-4 align-top whitespace-nowrap text-slate-700">
                        {fmt(u.createdAt)}
                      </td>

                      {/* Actions – compact icon buttons */}
                      <td className="px-6 py-4 align-top">
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => openEditFor(u)}
                            className="group inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                            title="Edit facilities"
                            aria-label="Edit facilities"
                          >
                            <IcEdit className="h-4 w-4" />
                            <span className="hidden lg:inline">Edit</span>
                          </button>

                          <button
                            onClick={() => openResetFor("landlord", u)}
                            className="group inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                            title="Reset password"
                            aria-label="Reset password"
                          >
                            <IcRefresh className="h-4 w-4" />
                            <span className="hidden lg:inline">Reset</span>
                          </button>

                          <button
                            onClick={() => openDeleteFor("landlord", u)}
                            className="group inline-flex h-8 items-center gap-1.5 rounded-lg bg-rose-600 px-2.5 text-xs font-semibold text-white hover:brightness-105 active:scale-[0.98] transition"
                            title="Delete user"
                            aria-label="Delete user"
                          >
                            <IcTrash className="h-4 w-4" />
                            <span className="hidden lg:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards keep the clean look */}
          {/* Mobile cards keep the clean look */}
          <div className="md:hidden p-4">
            <div className="grid gap-3">
              {lPage.length === 0 ? (
                <div className="rounded-2xl border border-black/[0.06] bg-white p-6 text-center text-black/60">
                  No landlords found.
                </div>
              ) : (
                lPage.map((u) => (
                  <LandlordCard
                    key={u.id}
                    user={u}
                    onEdit={() => openEditFor(u)}
                    onReset={() => openResetFor("landlord", u)}
                    onDelete={() => openDeleteFor("landlord", u)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="px-4 pb-4 pt-2">
            <Pager
              page={pageL}
              total={landlordsFiltered.length}
              pageSize={pageSize}
              onChange={setPageL}
            />
          </div>
        </section>
      ) : (
        <TowingUsersSection
          tPage={tPage}
          towersFiltered={towersFiltered}
          pageT={pageT}
          pageSize={pageSize}
          setPageT={setPageT}
          openEditFor={openEditFor}
          openResetFor={openResetFor}
          openDeleteFor={openDeleteFor}
        />
      )}

      {/* Add User Modal */}
      <Modal open={openAdd} onClose={closeAdd} title="Add a new user">
        <div className="relative">
          {/* Submitting overlay inside the modal */}

          {!selectedType && (
            <div className="grid gap-3 sm:grid-cols-2">
              <TypeCard
                icon={IcUser}
                title="Add Landlord"
                subtitle="Create a landlord login"
                onClick={() => setSelectedType("landlord")}
              />
              <TypeCard
                icon={IcBuilding}
                title="Add Towing User"
                subtitle="Create a towing company user"
                onClick={() => setSelectedType("towing")}
              />
            </div>
          )}

          {submitError && (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {submitError}
            </div>
          )}

          {selectedType === "landlord" && (
            <Formik
              initialValues={{
                email: "",
                password: "",
                facilities: [{ facility_id: "", facility_name: "" }],
              }}
              validationSchema={landlordSchema}
              onSubmit={async (vals, { setSubmitting, resetForm }) => {
                setSubmitError("");
                setSubmitting(true);
                setSubmittingApi(true);
                try {
                  const { uid: firebase_uid } = await ensureFirebaseUser(
                    vals.email.trim(),
                    vals.password,
                  );

                  // Normalize: keep only rows that have at least one field filled
                  const facilitiesClean = (vals.facilities || [])
                    .map((r) => ({
                      facility_id: String(r.facility_id || "").trim(),
                      facility_name: String(r.facility_name || "").trim(),
                    }))
                    .filter((r) => r.facility_id || r.facility_name);

                  const payload = {
                    email: vals.email.trim(),
                    role: "landlord",
                    firebase_uid,
                    ...(facilitiesClean.length
                      ? { facilities: facilitiesClean }
                      : {}),
                  };

                  await createUser(payload);
                  await loadUsers();
                  resetForm();
                  closeAdd();
                  setTab("landlord");
                } catch (e) {
                  setSubmitError(friendlyAuthError(e));
                } finally {
                  setSubmitting(false);
                  setSubmittingApi(false);
                }
              }}
            >
              {({ isSubmitting, values, setFieldValue, errors, touched }) => (
                <Form className="mt-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium">Email</label>
                    <Field
                      name="email"
                      type="email"
                      placeholder="landlord@company.com"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">
                      Password
                    </label>
                    <PasswordField
                      name="password"
                      placeholder="Enter a strong password"
                    />
                  </div>

                  {/* Facilities */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">
                        Facilities (required)
                      </label>
                      <span className="text-[11px] text-black/50">
                        Add one or many rows
                      </span>
                    </div>
                    <FacilitiesEditor
                      value={values.facilities}
                      onChange={(arr) => setFieldValue("facilities", arr)}
                      errorText={
                        // show either array-level error or a plain string error
                        typeof errors.facilities === "string"
                          ? errors.facilities
                          : undefined
                      }
                    />

                    {/* Row-level error (if any) */}
                    {typeof errors.facilities === "string" ? (
                      <div className="mt-1 text-xs text-rose-600">
                        {errors.facilities}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <button
                      type="button"
                      onClick={() => setSelectedType(null)}
                      className="text-sm text-black/60 hover:text-black"
                    >
                      ← Back
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={closeAdd}
                        className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || submittingApi}
                        className="rounded-xl bg-[color:var(--blue)] px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
                        style={{ "--blue": BLUE }}
                      >
                        Create Landlord
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {selectedType === "towing" && (
            <Formik
              initialValues={{
                companyName: "",
                email: "",
                password: "",
                facilities: [],
              }}
              validationSchema={towingSchema}
              onSubmit={async (vals, { setSubmitting, resetForm }) => {
                setSubmitError("");
                setSubmitting(true);
                setSubmittingApi(true);
                try {
                  const { uid: firebase_uid } = await ensureFirebaseUser(
                    vals.email.trim(),
                    vals.password,
                  );

                  const facilitiesClean = (vals.facilities || [])
                    .map((r) => ({
                      facility_id: String(r.facility_id || "").trim(),
                      facility_name: String(r.facility_name || "").trim(),
                    }))
                    .filter((r) => r.facility_id || r.facility_name);

                  await createUser({
                    email: vals.email.trim(),
                    role: "towing_company",
                    company_name: vals.companyName.trim(),
                    firebase_uid,
                    ...(facilitiesClean.length
                      ? { facilities: facilitiesClean }
                      : {}),
                  });

                  await loadUsers();
                  resetForm();
                  closeAdd();
                  setTab("towing");
                } catch (e) {
                  setSubmitError(e?.message || "Failed to create towing user");
                } finally {
                  setSubmitting(false);
                  setSubmittingApi(false);
                }
              }}
            >
              {({ isSubmitting, values, setFieldValue, errors }) => (
                <Form className="mt-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Towing company name
                    </label>
                    <Field
                      name="companyName"
                      placeholder="e.g. BlueLine Towing"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                    />
                    <ErrorMessage
                      name="companyName"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Email</label>
                    <Field
                      name="email"
                      type="email"
                      placeholder="ops@towingcompany.com"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Password
                    </label>
                    <PasswordField
                      name="password"
                      placeholder="Create a password"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">
                        Facilities (optional)
                      </label>
                      <span className="text-[11px] text-black/50">
                        Add one or many rows
                      </span>
                    </div>

                    <FacilitiesEditor
                      required={false}
                      value={values.facilities}
                      onChange={(arr) => setFieldValue("facilities", arr)}
                      errorText={
                        typeof errors.facilities === "string"
                          ? errors.facilities
                          : undefined
                      }
                    />
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <button
                      type="button"
                      onClick={() => setSelectedType(null)}
                      className="text-sm text-black/60 hover:text-black"
                    >
                      ← Back
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={closeAdd}
                        className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || submittingApi}
                        className="rounded-xl bg-[color:var(--blue)] px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
                        style={{ "--blue": BLUE }}
                      >
                        Create Towing User
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </Modal>

      {/* Reset Password Modal (mock) */}
      <Modal
        open={openReset}
        onClose={() => {
          setOpenReset(false);
          setResetTarget(null);
        }}
        title="Reset password"
      >
        {resetTarget && (
          <Formik
            initialValues={{ password: "" }}
            validationSchema={resetSchema}
            onSubmit={(vals, { setSubmitting, resetForm }) => {
              setSubmitting(false);
              resetForm();
              setOpenReset(false);
              setResetTarget(null);
              alert("Password reset saved (mock).");
            }}
          >
            {({ isSubmitting, values, setFieldValue, errors }) => (
              <Form className="space-y-4">
                <div className="text-sm text-black/70">
                  {resetTarget.kind === "towing" ? (
                    <>
                      User: <strong>{resetTarget.companyName}</strong> ·{" "}
                      {resetTarget.email}
                    </>
                  ) : (
                    <>
                      User: <strong>{resetTarget.email}</strong>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    New password
                  </label>
                  <PasswordField
                    name="password"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOpenReset(false);
                      setResetTarget(null);
                    }}
                    className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-xl bg-[color:var(--blue)] px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
                    style={{ "--blue": BLUE }}
                  >
                    Save password
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      <Modal
        open={openDelete}
        onClose={() => {
          if (deletingApi) return; // block closing while deleting
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        title="Delete user?"
      >
        <div className="space-y-4 relative">
          <p className="text-sm text-black/70">
            This will permanently remove the user from the system.
          </p>

          {deleteTarget && (
            <div className="rounded-xl border border-black/[0.06] bg-black/[0.02] px-3 py-2 text-sm">
              {deleteTarget.kind === "towing" ? (
                <>
                  Towing: <strong>{deleteTarget.companyName}</strong> ·{" "}
                  {deleteTarget.email}
                </>
              ) : (
                <>
                  Landlord: <strong>{deleteTarget.email}</strong>
                </>
              )}
              <div className="text-xs text-black/50 mt-1">
                UID: {deleteTarget.firebase_uid || "—"}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                if (deletingApi) return;
                setOpenDelete(false);
                setDeleteTarget(null);
              }}
              className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03]"
              disabled={deletingApi}
            >
              Cancel
            </button>

            <button
              onClick={doDelete}
              className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
              disabled={deletingApi || !deleteTarget?.firebase_uid}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Facilities (Landlord) */}
      <Modal
        open={openEdit}
        onClose={() => {
          if (updatingApi) return; // prevent closing while saving
          setOpenEdit(false);
          setEditTarget(null);
        }}
        title="Edit user facilities"
      >
        {editTarget && (
          <Formik
            enableReinitialize
            initialValues={{
              facilities:
                Array.isArray(editTarget.facilities) &&
                editTarget.facilities.length
                  ? editTarget.facilities.map((f) => ({
                      facility_id: String(f.facility_id || "").trim(),
                      facility_name: String(f.facility_name || "").trim(),
                    }))
                  : [{ facility_id: "", facility_name: "" }],
            }}
            validationSchema={Yup.object({
              facilities: Yup.array()
                .of(
                  Yup.object({
                    facility_id: Yup.string().trim().optional(),
                    facility_name: Yup.string().trim().optional(),
                  }).test(
                    "row-has-id-or-name",
                    "Each facility row needs an ID or a Name",
                    (v) =>
                      !!(v?.facility_id?.trim() || v?.facility_name?.trim()),
                  ),
                )
                .min(1, "Add at least one facility"),
            })}
            onSubmit={async (vals, { setSubmitting }) => {
              if (!editTarget.firebase_uid) {
                alert("Missing firebase_uid for this user.");
                return;
              }
              try {
                setSubmitting(true);
                setUpdatingApi(true);

                const facilitiesClean = (vals.facilities || [])
                  .map((r) => ({
                    facility_id: String(r.facility_id || "").trim(),
                    facility_name: String(r.facility_name || "").trim(),
                  }))
                  .filter((r) => r.facility_id || r.facility_name);

                await updateUser(editTarget.firebase_uid, {
                  facilities: facilitiesClean,
                });

                await loadUsers();
                setOpenEdit(false);
                setEditTarget(null);
              } catch (e) {
                alert(e?.message || "Failed to update facilities");
              } finally {
                setSubmitting(false);
                setUpdatingApi(false);
              }
            }}
          >
            {({ values, setFieldValue, errors, isSubmitting }) => (
              <Form className="space-y-4">
                {/* Who we are editing */}
                <div className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-black/[0.02] px-3 py-2">
                  <Avatar email={editTarget.email} />
                  <div className="text-sm">
                    <div className="font-medium">{editTarget.email}</div>
                    <div className="text-[11px] text-slate-500">
                      UID: {editTarget.firebase_uid?.slice(0, 12) || "—"}
                    </div>
                  </div>
                </div>

                {/* Editor */}
                <FacilitiesEditor
                  required={false}
                  value={values.facilities}
                  onChange={(arr) => setFieldValue("facilities", arr)}
                  errorText={
                    typeof errors.facilities === "string"
                      ? errors.facilities
                      : undefined
                  }
                />

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (updatingApi) return;
                      setOpenEdit(false);
                      setEditTarget(null);
                    }}
                    className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03]"
                    disabled={updatingApi}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || updatingApi}
                    className="rounded-xl bg-[color:var(--blue)] px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
                    style={{ "--blue": BLUE }}
                  >
                    Save changes
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </div>
  );
}
